import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { apiClient } from '../../../utils/apiClient';

interface FilterOption {
  label: string;
  value: string;
}

const MIN_INTERESTS = 3;
const MAX_INTERESTS = 10;
const INITIAL_VISIBLE = 12;
const LOAD_MORE_COUNT = 12;

const DIACRITICS_RE = /[̀-ͯ]/g;
const normalize = (str: string) => str.normalize('NFD').replace(DIACRITICS_RE, '').toLowerCase();

interface Props {
  currentInterests: string[];
  onSaved: (interests: string[]) => void;
}

const InterestsPanel: React.FC<Props> = ({ currentInterests, onSaved }) => {
  const [domains, setDomains] = useState<FilterOption[]>([]);
  const [selected, setSelected] = useState<string[]>(currentInterests);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setSelected(currentInterests);
  }, [currentInterests]);

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await apiClient('/jurisprudence-metadata/filters/legal-domains-ranked');
        if (!response.ok) throw new Error('Erreur de chargement');
        const data = await response.json();
        if (data.success && data.data?.legalDomains) {
          setDomains(data.data.legalDomains);
        }
      } catch {
        setFeedback({ msg: 'Impossible de charger les domaines.', type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchDomains();
  }, []);

  const filteredDomains = useMemo(() => {
    if (!searchQuery.trim()) return domains;
    const q = normalize(searchQuery);
    return domains.filter((d) => normalize(d.label).includes(q));
  }, [domains, searchQuery]);

  const displayedDomains = useMemo(() => {
    if (searchQuery.trim()) return filteredDomains;
    const selectedDomains = filteredDomains.filter((d) => selected.includes(d.value));
    const otherDomains = filteredDomains.filter((d) => !selected.includes(d.value));
    return [...selectedDomains, ...otherDomains.slice(0, visibleCount)];
  }, [filteredDomains, visibleCount, searchQuery, selected]);

  const hasMore = useMemo(() => {
    if (searchQuery.trim()) return false;
    const others = filteredDomains.filter((d) => !selected.includes(d.value));
    return visibleCount < others.length;
  }, [filteredDomains, visibleCount, searchQuery, selected]);
  const dirty = useMemo(() => {
    const a = [...selected].sort().join(',');
    const b = [...currentInterests].sort().join(',');
    return a !== b;
  }, [selected, currentInterests]);

  const toggleDomain = useCallback((value: string) => {
    setSelected((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= MAX_INTERESTS) return prev;
      return [...prev, value];
    });
  }, []);

  const handleSave = async () => {
    if (selected.length < MIN_INTERESTS) {
      setFeedback({ msg: `Sélectionnez au moins ${MIN_INTERESTS} centres d'intérêt.`, type: 'error' });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const res = await apiClient('/users/onboarding-interests', {
        method: 'POST',
        body: JSON.stringify({ onboardingInterests: selected }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Erreur lors de la sauvegarde');
      }
      onSaved(selected);
      setFeedback({ msg: "Centres d'intérêt mis à jour.", type: 'success' });
      setTimeout(() => setFeedback(null), 3000);
    } catch (err) {
      setFeedback({ msg: err instanceof Error ? err.message : 'Erreur', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-200">
        Ces thèmes alimentent les recommandations de dossiers et de jurisprudence qui vous sont proposés. Choisissez
        entre {MIN_INTERESTS} et {MAX_INTERESTS} domaines.
      </p>

      <input
        type="text"
        value={searchQuery}
        placeholder="Rechercher un domaine…"
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setVisibleCount(INITIAL_VISIBLE);
        }}
        className="w-full px-3 py-2 border border-gray-200 dark:border-mcdm-third-300 rounded-lg bg-white dark:bg-mcdm-third-300 text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-mcdm-secondary-300"
      />

      <div className="flex items-center justify-between text-xs text-mcdm-third-400 dark:text-mcdm-primary-300">
        <span>
          {selected.length}/{MAX_INTERESTS} sélectionnés
          {selected.length < MIN_INTERESTS && (
            <span className="ml-1 text-amber-600 dark:text-amber-400">(minimum {MIN_INTERESTS})</span>
          )}
        </span>
        {filteredDomains.length === 0 && !loading && <span>Aucun résultat</span>}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-6 text-center">Chargement des domaines…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {displayedDomains.map((d) => {
              const isSelected = selected.includes(d.value);
              const isDisabled = !isSelected && selected.length >= MAX_INTERESTS;
              return (
                <button
                  type="button"
                  key={d.value}
                  onClick={() => toggleDomain(d.value)}
                  disabled={isDisabled}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    isSelected
                      ? 'bg-mcdm-secondary-400 dark:bg-mcdm-secondary-500 text-white border-mcdm-secondary-400 dark:border-mcdm-secondary-500'
                      : isDisabled
                        ? 'bg-gray-50 dark:bg-mcdm-third-400 text-gray-400 border-gray-200 dark:border-mcdm-third-300 cursor-not-allowed'
                        : 'bg-white dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100 border-gray-200 dark:border-mcdm-third-300 hover:border-mcdm-secondary-300'
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          {hasMore && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => setVisibleCount((v) => v + LOAD_MORE_COUNT)}
                className="text-xs text-mcdm-secondary-300 hover:text-mcdm-secondary-400 underline"
              >
                Voir plus
              </button>
            </div>
          )}
        </>
      )}

      {feedback && (
        <div
          className={`text-sm rounded p-2 ${
            feedback.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {feedback.msg}
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2">
        {dirty && (
          <button
            type="button"
            onClick={() => setSelected(currentInterests)}
            disabled={saving}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            Annuler les changements
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty || selected.length < MIN_INTERESTS}
          className="px-4 py-2 text-sm bg-mcdm-green-400 hover:bg-mcdm-green-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
};

export default InterestsPanel;
